import { abbreviateNumber, safeGet } from "src/shared/utils";
import MetricOperations from "./MetricOperations";
import { IsTabHidden } from "src/shared/utils/customTab";

export interface ICategoryData {
  id: number;
  label: string;
  currentChartId: number;
  pastChartId: number;
  trendChartId: number;
  performanceChartIds: string;
  bigNumber?: string;
  vCompare?: string;
  progress?: boolean;
  successful?: boolean;
  isPercentage: boolean;
  isPositive: boolean;
  hasChart: boolean;
  isAnomalous: 0 | 1 | 2;
  isStaleMetric: string;
  lowerBound: number;
  upperBound: number;
  owner: string;
  dataset: string;
  freshness: number;
  sla: number;
  time_stamp: string;
  isHidden: string;
  message: string;
}
export interface ICategorialData {
  id: string;
  label: string;
  tiles: ICategoryData[];
}
export interface IAllOrgMetrics {
  categorialData: Array<ICategorialData>;
}
export interface IChartReqData {
  isHidden?: string;
  metric_id: number;
  slice_id: number;
  time_view: string;
  filters: {};
  data?: any;
}

type EachCategory = {
  key: string;
  value: ICategoryData[];
};

export type CategorialAPIData = IObject<ICategoryData[]>;

export default class AllOrgMetricsModel extends MetricOperations implements IAllOrgMetrics {
  public categorialData: Array<ICategorialData>;
  public static createAllOrgMetrics(data: any) {
    return new AllOrgMetricsModel(data);
  }
  public constructor(obj: any) {
    super();
    this.categorialData = [];
    if (obj.categorialData) {
      this.setCategorialData(obj.categorialData);
    }
  }

  public setCategorialData(data: Array<any>) {
    // FIXME:
    this.categorialData = (data || []).map((group) => {
      const key = Object.keys(group)[0];
      const metrics = this.setTiles(group[key] || []);

      return {
        id: key,
        tiles: metrics,
        label: key,
      };
    });
  }

  public setTiles(data: ICategoryData[]) {
    return data.map((tile) => ({ ...tile, hasChart: false }));
  }

  public setNumbers(data, timeTab): void {
    this.categorialData.forEach(({ tiles }) => {
      tiles.forEach((set) => {
        const selectedArray: any = [];
        const firstElement = data.filter(
          ({ metric_id, slice_id, time_view, isHidden }) =>
            metric_id === set.id && slice_id === set.currentChartId && time_view === "currentChart"
        );
        const secondElement = data.filter(
          ({ metric_id, slice_id, time_view, isHidden }) =>
            metric_id === set.id && slice_id === set.pastChartId && time_view === "pastChart"
        );
        selectedArray.push(firstElement, secondElement);
        const bigNumber = safeGet(selectedArray, `[0][0].data.data.data[0][0]`, 0);

        selectedArray.forEach((element) => {
          if (!set.isStaleMetric) {
            const chartData = element[0];
            // https://swiggy.atlassian.net/browse/ANOBIS-224
            if (chartData && (chartData.time_view === "currentChart" || chartData.time_view === "trendChart")) {
              set["isStaleMetric"] = chartData.isStaleMetric;
            }
          }
        });
        const vCompare = safeGet(selectedArray, `[1][0].data.data.data[0][0]`);
        set["bigNumber"] = !IsTabHidden(safeGet(selectedArray, `[0][0].isHidden`, "1111"), timeTab)
          ? set.isPercentage
            ? abbreviateNumber(bigNumber).concat("%")
            : abbreviateNumber(bigNumber)
          : "N/A";
        const isPastNumberPresent = safeGet(selectedArray, `[1][0].data.data.data[0][0]`, false) !== false;
        const cPrefix = set.isPercentage ? MetricOperations.comparePrefixPercent : MetricOperations.comparePrefix;
        const vCompValue = set.isPercentage
          ? bigNumber - vCompare
          : vCompare === 0
          ? 100
          : ((bigNumber - vCompare) * 100) / vCompare;
        set["vCompare"] = !IsTabHidden(safeGet(selectedArray, `[0][0].isHidden`, "1111"), timeTab)
          ? isPastNumberPresent
            ? abbreviateNumber(vCompValue).concat(cPrefix[timeTab])
            : ""
          : "";
        set["progress"] = bigNumber - vCompare > 0;
        set["successful"] = safeGet(selectedArray, `[0][0].data.data.data[0][0]`, false) !== false;
        set["hasChart"] = true;

        set["isAnomalous"] = +vCompValue < set.lowerBound || +vCompValue > set.upperBound ? 2 : set.isAnomalous ? 1 : 0;
      });
    });
  }

  public prepareChartData(tab: string, filterConfig, permittedCities: string[] = []) {
    let bulkChartReqData: IChartReqData[] = [];
    let categories = this.categorialData;

    const filters = permittedCities.length > 0 ? [{ field: "city", value: permittedCities }] : [];
    categories.forEach(({ tiles }) => {
      tiles.reduce((accumulator, eachTile) => {
        if (eachTile.currentChartId)
          accumulator.push({
            isHidden: eachTile.isHidden,
            metric_id: eachTile.id,
            slice_id: eachTile.currentChartId,
            time_view: "currentChart",
            filters: {
              adhoc_filters: filters,
              chronic_filters: MetricOperations.parseChronicFilter(filterConfig, tab, "currentChart"),
              //MetricOperations.chronicFilterValues[tab]
            },
          });

        if (eachTile.pastChartId)
          accumulator.push({
            isHidden: eachTile.isHidden,
            metric_id: eachTile.id,
            slice_id: eachTile.pastChartId,
            time_view: "pastChart",
            filters: {
              adhoc_filters: filters,
              chronic_filters: MetricOperations.parseChronicFilter(filterConfig, tab, "pastChart"), //MetricOperations.pastChronicFilterValues[tab]
            },
          });
        return accumulator;
      }, bulkChartReqData);
    });
    return bulkChartReqData;
  }
}
